(() => {
  // The offline renderer imports this same motion model. No labels are generated.
  const GIGI_CANNING = Object.freeze({
    width: 1536, height: 512, canWidth: 27, canHeight: 68, canY: 235,
    exitX: 1064, exitY: 208, spacing: 37, minimumGap: 30, gravity: 760,
    fillPitch: 49, sealPitch: 37, sealStart: 916, fillPeriod: 1.3,
    heads: [534, 583, 632, 681, 730],
    flavours: ['lemon-lime', 'pineapple-coconut', 'wildberry'],
  });

  const canningEase = value => value * value * (3 - 2 * value);
  const canningClamp = value => Math.max(0, Math.min(1, value));

  class GigiCanningMotion {
    constructor() {
      this.time = 0;
      this.sequence = 0;
      this.beltTravel = 0;
      this.cans = Array.from({ length: 13 }, (_, index) => this.makeCan(
        1112 + index * GIGI_CANNING.spacing,
        GIGI_CANNING.canY,
      ));
    }

    makeCan(x, y) {
      return { x, y, vy: 0, speed: 60, landed: y === GIGI_CANNING.canY,
        age: y === GIGI_CANNING.canY ? 1 : 0,
        flavour: this.sequence++ % GIGI_CANNING.flavours.length, tilt: 0 };
    }

    step(elapsed) {
      // Fixed, bounded substeps keep collision and gravity behavior independent of refresh rate.
      while (elapsed > 0.000001) {
        const dt = Math.min(elapsed, 1 / 120);
        elapsed -= dt;
        this.time += dt;
        const beltSpeed = 66 + 9 * Math.sin(this.time * 1.4);
        this.beltTravel += beltSpeed * dt;
        const gateClosed = this.time % 7.8 > 3.7 && this.time % 7.8 < 4.45;
        this.cans.sort((a, b) => b.x - a.x);
        let ahead;
        for (const can of this.cans) {
          can.age += dt;
          const gap = ahead ? ahead.x - can.x : Infinity;
          const waiting = gateClosed && can.x > 1228 && can.x < 1264;
          const target = waiting ? 0 : Math.min(beltSpeed, Math.max(0, (gap - GIGI_CANNING.minimumGap) * 9));
          can.speed += (target - can.speed) * Math.min(1, dt * 10);
          can.x += can.speed * dt;
          if (ahead && can.x > ahead.x - GIGI_CANNING.minimumGap) {
            can.x = ahead.x - GIGI_CANNING.minimumGap;
            can.speed = Math.min(can.speed, ahead.speed);
          }
          if (!can.landed) {
            can.vy += GIGI_CANNING.gravity * dt;
            can.y += can.vy * dt;
            if (can.y >= GIGI_CANNING.canY) {
              can.y = GIGI_CANNING.canY;
              can.vy = -can.vy * 0.14;
              if (Math.abs(can.vy) < 12) { can.vy = 0; can.landed = true; }
            }
            can.tilt = Math.max(-2, Math.min(2, can.vy * 0.004));
          } else {
            can.tilt *= Math.exp(-dt * 12);
          }
          ahead = can;
        }
        this.cans = this.cans.filter(can => can.x < GIGI_CANNING.width + 45);
        const last = this.cans[this.cans.length - 1];
        // A blocked outlet waits for space instead of dropping cans through one another.
        if (!last || (last.landed && last.x > GIGI_CANNING.exitX + GIGI_CANNING.spacing)) {
          const can = this.makeCan(GIGI_CANNING.exitX, GIGI_CANNING.exitY);
          can.speed = beltSpeed;
          this.cans.push(can);
        }
      }
    }

    frame() {
      const phase = (this.time % GIGI_CANNING.fillPeriod) / GIGI_CANNING.fillPeriod;
      const lowering = canningEase(canningClamp((phase - 0.05) / 0.15));
      const retracting = canningEase(canningClamp((phase - 0.51) / 0.18));
      const index = canningEase(canningClamp((phase - 0.72) / 0.28));
      const incoming = Array.from({ length: 24 }, (_, slot) => {
        const position = slot - 11 + index;
        const start = GIGI_CANNING.heads[0] - 13;
        if (position <= 5) return start + position * GIGI_CANNING.fillPitch;
        if (position >= 8) return GIGI_CANNING.sealStart + (position - 8) * GIGI_CANNING.sealPitch;
        // One continuous train changes pitch between stations. Hermite interpolation
        // matches both position and speed at the ends, so no can is cut or swapped.
        const t = (position - 5) / 3;
        const t2 = t * t, t3 = t2 * t;
        return (2 * t3 - 3 * t2 + 1) * (start + 5 * GIGI_CANNING.fillPitch)
          + (t3 - 2 * t2 + t) * 3 * GIGI_CANNING.fillPitch
          + (-2 * t3 + 3 * t2) * GIGI_CANNING.sealStart
          + (t3 - t2) * 3 * GIGI_CANNING.sealPitch;
      });
      return {
        cans: this.cans,
        incoming,
        heads: -60 + 36 * (lowering - retracting),
        filling: phase >= 0.2 && phase <= 0.51,
        // The sealing chucks touch the rims while the indexed cans are stopped.
        seamer: -24 + 13 * (lowering - retracting),
        belt: this.beltTravel % 14,
      };
    }
  }

  if (typeof module !== 'undefined') module.exports = { GIGI_CANNING, GigiCanningMotion };

  if (typeof customElements !== 'undefined' && !customElements.get('gigi-canning-line')) {
    customElements.define('gigi-canning-line', class extends HTMLElement {
      connectedCallback() {
        this.stage = this.querySelector('.gigi-canning-line__stage');
        this.button = this.querySelector('button');
        if (!this.stage || !this.button) return;

        this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.userPaused = this.motion.matches;
        this.inView = false;
        this.ready = this.dataset.ready === 'true';
        this.loading = false;
        this.machine = new GigiCanningMotion();
        this.canSlots = [...this.querySelectorAll('[data-canning-can]')];
        this.emptyCans = [...this.querySelectorAll('[data-canning-incoming] use')];
        this.heads = this.querySelector('[data-canning-heads]');
        this.jets = this.querySelector('[data-canning-jets]');
        this.seamer = this.querySelector('[data-canning-seamer]');
        this.belt = this.querySelector('[data-canning-belt]');
        this.renderFrame();
        this.events = new AbortController();
        const options = { signal: this.events.signal };

        this.button.addEventListener('click', () => {
          this.userPaused = this.dataset.playing === 'true';
          this.updatePlayback();
        }, options);
        document.addEventListener('visibilitychange', () => this.updatePlayback(), options);
        this.motion.addEventListener('change', () => {
          this.userPaused = this.motion.matches;
          this.updatePlayback();
        }, options);

        this.observer = new IntersectionObserver(([entry]) => {
          this.inView = entry.isIntersecting;
          if (this.inView && !this.ready && !this.loading) this.loadScene();
          this.updatePlayback();
        }, { threshold: 0.1 });
        this.observer.observe(this.stage);
        this.button.hidden = !this.ready;
        this.updatePlayback();
      }

      async loadScene() {
        this.loading = true;
        const events = this.events;
        const sources = [...this.querySelectorAll('image[data-href]')];
        const urls = [...new Set(sources.map(source => source.dataset.href))];
        const results = await Promise.allSettled(urls.map(url => {
          const image = new Image();
          image.src = url;
          return image.decode();
        }));
        if (!this.isConnected || this.events !== events || results.some(result => result.status === 'rejected')) return;
        sources.forEach(source => source.setAttribute('href', source.dataset.href));
        this.ready = true;
        this.dataset.ready = 'true';
        this.button.hidden = false;
        this.updatePlayback();
      }

      disconnectedCallback() {
        this.observer?.disconnect();
        this.events?.abort();
        this.dataset.playing = 'false';
        cancelAnimationFrame(this.animation);
        this.animation = null;
      }

      updatePlayback() {
        const playing = this.ready && !this.userPaused && this.inView && !document.hidden;
        this.dataset.playing = String(playing);
        this.button.querySelector('[data-canning-line-label]').textContent = playing ? 'Pause animation' : 'Play animation';
        if (playing && !this.animation) {
          this.lastFrame = null;
          this.animation = requestAnimationFrame(time => this.tick(time));
        } else if (!playing) {
          cancelAnimationFrame(this.animation);
          this.animation = null;
        }
      }

      tick(time) {
        if (this.lastFrame !== null) this.machine.step(Math.min((time - this.lastFrame) / 1000, 0.05));
        this.lastFrame = time;
        this.renderFrame();
        this.animation = requestAnimationFrame(next => this.tick(next));
      }

      renderFrame() {
        const frame = this.machine.frame();
        this.emptyCans.forEach((can, index) => can.setAttribute('transform',
          `translate(${frame.incoming[index].toFixed(2)} ${GIGI_CANNING.canY})`));
        this.heads.setAttribute('transform', `translate(0 ${frame.heads.toFixed(2)})`);
        this.jets.setAttribute('opacity', frame.filling ? '0.8' : '0');
        this.seamer.setAttribute('transform', `translate(0 ${frame.seamer.toFixed(2)})`);
        this.belt.setAttribute('transform', `translate(${frame.belt.toFixed(2)} 0)`);
        this.canSlots.forEach((slot, index) => {
          const can = frame.cans[index];
          // SVG children with visibility="visible" can override a hidden parent.
          // display="none" removes the entire inactive slot, including its artwork.
          slot.setAttribute('display', can ? 'inline' : 'none');
          if (!can) return;
          slot.setAttribute('transform', `translate(${can.x.toFixed(2)} ${can.y.toFixed(2)}) rotate(${can.tilt.toFixed(2)} 13.5 68)`);
          slot.setAttribute('opacity', canningEase(canningClamp(can.age / 0.08)).toFixed(2));
          const flavour = String(can.flavour);
          if (slot.dataset.flavour !== flavour) {
            slot.dataset.flavour = flavour;
            [...slot.children].forEach((artwork, imageIndex) => artwork.setAttribute('visibility', imageIndex === can.flavour ? 'visible' : 'hidden'));
          }
        });
      }
    });
  }
})();
